import { z } from "zod";
import { OrderDelivery } from "./checkout.js";
import {
  FabricTypeSchema,
  OrderStatusSchema,
  PillowShapeSchema,
  ProductionFileKindSchema,
  ProductionNoteSourceSchema,
} from "./enums.js";
import { PRODUCTION_TASK_KINDS } from "./fulfillment.js";
import { FulfillmentPathSchema } from "./products.js";

// Contracts for the internal admin console (issue #238). Shared so the API and
// the `packages/admin` SPA validate the same shapes — the same pattern the
// public frontend follows for its API contracts.

// `GET /admin/me` — the resolved admin actor (dev actor, configured identity, or
// Cloudflare Access email depending on ADMIN_AUTH_MODE). Later slices stamp this
// onto `order_events.actor` / `settings.updated_by`.
export const AdminMeResponse = z.object({ actor: z.string() });
export type AdminMeResponse = z.infer<typeof AdminMeResponse>;

// `POST /admin/login` (password mode) — exchanges the env credential for a
// signed httpOnly session cookie.
export const AdminLoginRequest = z.object({ password: z.string().min(1) });
export type AdminLoginRequest = z.infer<typeof AdminLoginRequest>;

export const AdminLoginResponse = z.object({ ok: z.literal(true) });
export type AdminLoginResponse = z.infer<typeof AdminLoginResponse>;

// Admin error envelope — deliberately decoupled from the public
// `API_ERROR_CODES` catalogue (which the frontend maps to localized i18n copy).
// Admin codes are free-form strings rendered directly by the admin console.
export const AdminErrorEnvelope = z.object({
  error: z.object({ code: z.string(), message: z.string().optional() }),
});
export type AdminErrorEnvelope = z.infer<typeof AdminErrorEnvelope>;

// Feature-flags tab (issue #241) — an admin surface over the existing
// `feature_flags` kill-switch table (ADR-0031), deliberately separate from
// Settings (booleans-as-kill-switches vs product config). `enabled` is the raw
// switch state: `true` means the kill-switch is ACTIVE (e.g. `payments_disabled`
// enabled ⇒ payments off), so the UI shows this value directly and never
// inverts it. `updatedAt` is an ISO-8601 string; `description`/`updatedBy` are
// nullable (the column allows null and a never-flipped flag has no actor).
export const AdminFeatureFlag = z.object({
  name: z.string(),
  enabled: z.boolean(),
  description: z.string().nullable(),
  updatedAt: z.string(),
  updatedBy: z.string().nullable(),
});
export type AdminFeatureFlag = z.infer<typeof AdminFeatureFlag>;

// Response of `GET /admin/feature-flags` — every row of the table, name-ordered.
export const AdminFeatureFlagsResponse = z.object({ flags: z.array(AdminFeatureFlag) });
export type AdminFeatureFlagsResponse = z.infer<typeof AdminFeatureFlagsResponse>;

// Body of `PUT /admin/feature-flags/{name}` — the desired switch state. The
// handler stamps `updated_by` from the `requireAdmin` actor + `updated_at`.
export const AdminFeatureFlagUpdateRequest = z.object({ enabled: z.boolean() });
export type AdminFeatureFlagUpdateRequest = z.infer<typeof AdminFeatureFlagUpdateRequest>;

// Katalog tab (issue #404, ADR-0054 §6) — the admin surface over the EXISTING
// `product_types` catalogue and their generic **Variants**. It extends the old
// edit-only product-variants tab (#242): the operator now edits type metadata + an
// active toggle (which governs what the buyer can pick) alongside each variant's
// price / currency / attributes. **Creating or deleting a product type is NOT an
// admin action** — a new type needs a migration/seed row plus its bespoke build, so
// there is no create/delete contract here. Edits never rewrite placed orders
// (`order_lines.unit_price_cents` is snapshotted at checkout) and the buyer catalog
// reads the tables live (no cache), so a price / active change lands on the next quote.

// A Variant's generic per-type attributes (ADR-0054): a free jsonb object interpreted
// per product type (a pillow's `{ shape, size_code, size_cm }`; a future type's own
// axes). Kept an open record so a per-variant axis added later (e.g. the #406 shipping
// size class) is editable here with no schema change.
export const AdminCatalogAttributes = z.record(z.string(), z.unknown());
export type AdminCatalogAttributes = z.infer<typeof AdminCatalogAttributes>;

// One priced Variant row of a Product type, as the Katalog tab reads it. `productType`
// + `id` are the immutable identity; `attributes` / `basePriceCents` / `currency` are
// the mutable fields.
export const AdminCatalogVariant = z.object({
  id: z.guid(),
  productType: z.string(),
  attributes: AdminCatalogAttributes,
  basePriceCents: z.number().int().nonnegative(),
  currency: z.string(),
});
export type AdminCatalogVariant = z.infer<typeof AdminCatalogVariant>;

// One Product type with its Variants. `displayName` / `fulfillmentPath` /
// `shippingSizeClass` / `active` are the operator-editable metadata; `id` / `custom` /
// `variantSchema` are immutable descriptors (a type reshape is a migration + bespoke
// build). `shippingSizeClass` lives at the TYPE level (ADR-0054 §1 / #402); the
// per-variant oversize summation is #406's job — a per-variant class, if added, lands
// inside each variant's `attributes`, which this tab already edits generically.
export const AdminCatalogType = z.object({
  id: z.string(),
  displayName: z.string(),
  fulfillmentPath: FulfillmentPathSchema,
  shippingSizeClass: z.string(),
  active: z.boolean(),
  custom: z.boolean(),
  variantSchema: z.unknown(),
  variants: z.array(AdminCatalogVariant),
});
export type AdminCatalogType = z.infer<typeof AdminCatalogType>;

// Response of `GET /admin/catalog` — every product type (id-ordered) with its Variants.
// The whole catalogue is small + bounded, so no pagination.
export const AdminCatalogResponse = z.object({
  types: z.array(AdminCatalogType),
});
export type AdminCatalogResponse = z.infer<typeof AdminCatalogResponse>;

// Body of `PUT /admin/catalog/product-types/{id}` — the mutable type metadata + the
// active toggle, replaced as a unit. `displayName` / `shippingSizeClass` are non-empty
// (trimmed); `fulfillmentPath` is validated against the shared two-value set (mirrors
// the DB CHECK); `active` gates buyer visibility server-side.
export const AdminCatalogTypeUpdateRequest = z.object({
  displayName: z.string().trim().min(1),
  fulfillmentPath: FulfillmentPathSchema,
  shippingSizeClass: z.string().trim().min(1),
  active: z.boolean(),
});
export type AdminCatalogTypeUpdateRequest = z.infer<typeof AdminCatalogTypeUpdateRequest>;

// Body of `PUT /admin/catalog/variants/{id}` — the mutable variant fields, replaced as
// a unit. `basePriceCents` must be a non-negative integer (mirrors the DB CHECK);
// `currency` is a non-empty code (free `text`, default PLN); `attributes` is the generic
// per-type axes object (the server rejects a collision with another variant of the same
// type — the `UNIQUE(product_type, attributes)` identity).
export const AdminCatalogVariantUpdateRequest = z.object({
  basePriceCents: z.number().int().nonnegative(),
  currency: z.string().trim().min(1),
  attributes: AdminCatalogAttributes,
});
export type AdminCatalogVariantUpdateRequest = z.infer<typeof AdminCatalogVariantUpdateRequest>;

// Read-only order views for the ops console (issue #239). All timestamps are
// ISO-8601 strings (the API serializes the `timestamptz` columns with
// `Date.toISOString`) so the wire shape is JSON-native and the SPA parses them
// with `new Date(...)` for display.

// One row of `GET /admin/orders` — the columns the filterable order table shows.
// `buyerEmail` is nullable: `orders.user_id` is SET NULL on a GDPR user delete
// (ADR-0019), so a retained order can outlive its buyer identity.
export const AdminOrderListItem = z.object({
  id: z.guid(),
  status: OrderStatusSchema,
  buyerEmail: z.string().nullable(),
  totalAmountCents: z.number().int().nonnegative(),
  currency: z.string(),
  createdAt: z.string(),
  paidAt: z.string().nullable(),
});
export type AdminOrderListItem = z.infer<typeof AdminOrderListItem>;

// Fixed server-side page size for the admin order list (#245). The list is
// paginated so it stays responsive as orders accumulate; the response carries
// `total` / `page` so the SPA can render prev-next controls.
export const ADMIN_ORDERS_PAGE_SIZE = 20;

// Response of `GET /admin/orders` — one page of orders, newest first, optionally
// filtered by `?status=`. `total` is the full count for the active filter (drives
// the page count), `page` is 1-based, `pageSize` echoes the server page size.
export const AdminOrderListResponse = z.object({
  orders: z.array(AdminOrderListItem),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type AdminOrderListResponse = z.infer<typeof AdminOrderListResponse>;

// Query of `GET /admin/orders` — an optional `order_status` filter + a 1-based
// `page` (default 1). Omitted status ⇒ all orders.
export const AdminOrderListQuery = z.object({
  status: OrderStatusSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
});
export type AdminOrderListQuery = z.infer<typeof AdminOrderListQuery>;

// One purchased line of an order detail: quantity + snapshotted unit price plus
// the resolved `product_variants` row (size + the type-specific axes) and the
// project's freshly-signed rendered thumbnail (null when no thumbnail exists yet —
// the UI shows a quiet placeholder). An order can now MIX product types (ADR-0054,
// #408), so the pillow-only axes are nullable: `shape` / `fabricType` are the pillow's
// (null for a 3D print, which prices on colour depth); `productType` names the kind so
// the UI titles + groups the line. `sizeCode` / `sizeCm` are carried by every type.
// `projectId` is nullable defensively (the join is a left join) though
// `order_lines.project_id` RESTRICTs in practice.
export const AdminOrderLine = z.object({
  id: z.guid(),
  productType: z.string().nullable(),
  quantity: z.number().int().positive(),
  unitPriceCents: z.number().int().nonnegative(),
  shape: PillowShapeSchema.nullable(),
  fabricType: FabricTypeSchema.nullable(),
  sizeCode: z.string(),
  sizeCm: z.number().int().positive(),
  projectId: z.guid().nullable(),
  thumbnailUrl: z.string().url().nullable(),
});
export type AdminOrderLine = z.infer<typeof AdminOrderLine>;

// One entry of the order-detail timeline. Deliberately source-tagged and
// normalized so the timeline component can later MERGE additional feeds (the
// dedicated notifications table lands on another track): only `order_events`
// feeds it today (`source: "order_event"`), but a future source concatenates
// entries with its own tag and the UI sorts the union by `at`. `id` is a string
// because `order_events.id` is a `bigserial` (bigint) — serialized as text so it
// survives JSON. `payload` is the raw event jsonb (or null). `actor` is who
// caused the event: the `requireAdmin` identity for a manual step, coalesced to
// `"system"` for app/webhook events (and any row predating the `actor` column).
export const AdminTimelineEntry = z.object({
  source: z.string(),
  id: z.string(),
  type: z.string(),
  payload: z.unknown(),
  at: z.string(),
  actor: z.string(),
});
export type AdminTimelineEntry = z.infer<typeof AdminTimelineEntry>;

// One internal note (issue #244) as it crosses the wire: its stable id plus the
// operator's free text and who/when wrote it. Notes are append-only
// `internal_note` order events surfaced as a distinct thread (drawn from the same
// `order_events` log as the timeline, but not mixed into it). `id` is the event's
// `bigserial` serialized as text (mirroring `AdminTimelineEntry.id`). `actor` is
// non-null — a note is always written behind `requireAdmin` — but the projection
// coalesces a null column to `"system"` defensively, matching the timeline.
export const AdminOrderNote = z.object({
  id: z.string(),
  text: z.string(),
  at: z.string(),
  actor: z.string(),
});
export type AdminOrderNote = z.infer<typeof AdminOrderNote>;

// Body of `POST /admin/orders/:id/notes` — append one internal note. `text` is
// trimmed + required (an empty note is meaningless) and capped so a runaway
// paste can't bloat the event payload. The handler appends an `internal_note`
// event with `{ text }` + the `requireAdmin` actor and responds with the
// re-projected order detail (so the caller drops it straight into the cache).
export const AdminOrderNoteRequest = z.object({
  text: z.string().trim().min(1).max(4000),
});
export type AdminOrderNoteRequest = z.infer<typeof AdminOrderNoteRequest>;

// One projected fulfillment-checklist step (issue #243) as it crosses the wire:
// only the dynamic state. The static bits (label, order, status mapping, whether
// it's operator-tickable) live in the shared `FULFILLMENT_STEPS` registry, which
// the SPA imports directly and joins by `key`. `done` is last-event-wins over
// the event log; `at` / `actor` are non-null exactly when the step is done.
export const AdminFulfillmentStep = z.object({
  key: z.string(),
  done: z.boolean(),
  at: z.string().nullable(),
  actor: z.string().nullable(),
});
export type AdminFulfillmentStep = z.infer<typeof AdminFulfillmentStep>;

// One line's per-type production task (ADR-0054 §5, #408): the `order_lines.id` it's
// for, its `taskKind` (producer packet vs print — derived from the type's fulfillment
// path), the line-scoped steps that kind includes (each with THIS line's done-state,
// joined to `FULFILLMENT_STEPS` by key for its label like the order-level checklist),
// and whether the line's production is complete. The order-level status advances only
// once every line's task is `done`. `steps` carry the per-line `order_events` tagged
// with this line; the customer Order view reads the SAME projection (coarse status).
export const AdminFulfillmentLineTask = z.object({
  orderLineId: z.guid(),
  taskKind: z.enum(PRODUCTION_TASK_KINDS),
  steps: z.array(AdminFulfillmentStep),
  done: z.boolean(),
});
export type AdminFulfillmentLineTask = z.infer<typeof AdminFulfillmentLineTask>;

// Body of `POST /admin/orders/:id/steps` — tick or un-tick one checklist step.
// `step` is a registry key (validated server-side; an unknown or system-managed
// step is rejected). The handler runs the pure resolver, then appends the
// `order_event` (with the actor) and applies any coarse status change in one
// transaction, and responds with the freshly re-projected order detail.
//
// `trackingNumber` / `trackingCarrier` register the shipment (#315): the operator
// supplies them WHEN ticking `handed_to_carrier`, so the parcel handle is captured
// onto `orders` in the same transaction and the shipped email can carry it. They
// are optional (meaningful only for that step's tick — ignored on every other step
// and on an un-tick); `trackingCarrier` defaults to InPost, the single-carrier MVP.
//
// `orderLineId` targets a per-line PRODUCTION step (ADR-0054 §5, #408): required for a
// line-scoped step (the appended `order_events` row is tagged with it, and the order
// status advances only once every line has reached the mapped step), omitted for an
// order-scoped step. The server validates it belongs to the order + matches the step's
// scope, so the client can't tag a logistics step or a foreign line.
export const AdminOrderStepRequest = z.object({
  step: z.string(),
  action: z.enum(["tick", "untick"]),
  orderLineId: z.guid().optional(),
  trackingNumber: z.string().trim().min(1).max(64).optional(),
  trackingCarrier: z.string().trim().min(1).max(64).optional(),
});
export type AdminOrderStepRequest = z.infer<typeof AdminOrderStepRequest>;

// Factory-files admin surface (#282, over the #281 generator + tables). One
// producer-packet file (`production_files`) as it crosses the wire: its stable
// id + kind, the line it belongs to (null for the order-level `spec.pdf`), the
// mime + byte size, a freshly-signed URL for inline preview / per-file download
// (minted with the same `AssetUrlSigner` the job manifest uses), and the row's
// timestamps as ISO-8601 strings. `orderLineId` is nullable exactly like the
// column: the four per-line artifacts carry the line, the `spec.pdf` carries null.
export const AdminProductionFile = z.object({
  id: z.guid(),
  orderLineId: z.guid().nullable(),
  kind: ProductionFileKindSchema,
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  url: z.string().url(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AdminProductionFile = z.infer<typeof AdminProductionFile>;

// Response of `GET /admin/orders/:id/production-files` — the current tracked
// packet, oldest-first (repo order), each with a freshly-signed URL.
export const AdminProductionFilesResponse = z.object({
  files: z.array(AdminProductionFile),
});
export type AdminProductionFilesResponse = z.infer<typeof AdminProductionFilesResponse>;

// One producer-channel note (`production_notes`) — an operator note attached in
// the console or a factory reply logged against the order. Append-only (never
// edited or deleted), so the thread is a permanent record. Kept separate from
// the customer-facing `order_events` timeline. `author` is nullable (a factory
// reply may carry no attributed author); `orderLineId` is nullable (a note can
// be about the whole order or one line). `createdAt` is an ISO-8601 string.
export const AdminProductionNote = z.object({
  id: z.guid(),
  orderLineId: z.guid().nullable(),
  source: ProductionNoteSourceSchema,
  body: z.string(),
  author: z.string().nullable(),
  createdAt: z.string(),
});
export type AdminProductionNote = z.infer<typeof AdminProductionNote>;

// Response of `GET /admin/orders/:id/production-notes` — the whole thread,
// chronological.
export const AdminProductionNotesResponse = z.object({
  notes: z.array(AdminProductionNote),
});
export type AdminProductionNotesResponse = z.infer<typeof AdminProductionNotesResponse>;

// Body of `POST /admin/orders/:id/production-notes` — append one note.
// `source` tags who authored it (operator | factory). `body` is trimmed +
// required + capped (a runaway paste can't bloat the column). `orderLineId` is
// optional (scoped to a line) and validated server-side to belong to the order.
export const AdminProductionNoteRequest = z.object({
  source: ProductionNoteSourceSchema,
  body: z.string().trim().min(1).max(4000),
  orderLineId: z.guid().nullable().optional(),
});
export type AdminProductionNoteRequest = z.infer<typeof AdminProductionNoteRequest>;

// Response of `GET /admin/orders/:id` (and of `POST /admin/orders/:id/steps`) —
// the full picture of one order, assembled across `orders`, `order_lines`,
// `product_variants`, `projects`, `users`, and `order_events` (ADR-0018). `status`
// is the persisted coarse status, which the fulfillment checklist (#243) keeps in
// step with `fulfillment` — the projection of the event log over the shared
// registry. `fulfillment` lists every registry step in checklist order; `notes`
// is the append-only internal-notes thread (#244), chronological.
export const AdminOrderDetail = z.object({
  id: z.guid(),
  status: OrderStatusSchema,
  buyerEmail: z.string().nullable(),
  totalAmountCents: z.number().int().nonnegative(),
  currency: z.string(),
  createdAt: z.string(),
  paidAt: z.string().nullable(),
  stripeCheckoutSessionId: z.string().nullable(),
  stripePaymentIntentId: z.string().nullable(),
  // The InPost Delivery captured on our checkout (#255) — always present (the
  // `orders.delivery` column is NOT NULL). The admin order-detail renders the
  // point/address + receiver + phone. `shippingAmountCents` is the flat shipping
  // charged (a separate Stripe line item), shown alongside the method.
  delivery: OrderDelivery,
  shippingAmountCents: z.number().int().nonnegative(),
  // The shipment parcel handle captured when the operator ticks `handed_to_carrier`
  // (#315): the InPost tracking number + carrier the shipped email carries, echoed
  // back so the operator can see (and re-verify) what was sent. Null until shipped.
  trackingNumber: z.string().nullable(),
  trackingCarrier: z.string().nullable(),
  lines: z.array(AdminOrderLine),
  timeline: z.array(AdminTimelineEntry),
  // `fulfillment` is now the ORDER-LEVEL ladder only (the auto lifecycle + the
  // logistics steps packed/shipped/delivered — ADR-0054 §5, #408); the per-line, per-
  // type production tasks moved to `fulfillmentLines`. The SPA joins both to
  // `FULFILLMENT_STEPS` by key (scope tells it which list a step renders in).
  fulfillment: z.array(AdminFulfillmentStep),
  // The per-line production tasks (#408), one per order line, each with its type task
  // (producer packet / print) + the line's step states. Empty for an order with no
  // lines. The admin groups these by project; the order status advances once all `done`.
  fulfillmentLines: z.array(AdminFulfillmentLineTask),
  notes: z.array(AdminOrderNote),
  // The factory-files packet (#282): the tracked `production_files` (each with a
  // freshly-signed URL) + the append-only `production_notes` thread. Always
  // present — empty arrays until the operator runs the generator / adds a note.
  productionFiles: z.array(AdminProductionFile),
  productionNotes: z.array(AdminProductionNote),
});
export type AdminOrderDetail = z.infer<typeof AdminOrderDetail>;

// Dashboard stats (#245) — a read-only overview for the Pulpit. All aggregation
// happens server-side; the SPA only renders. Amounts are in grosze; `currency`
// is the shop currency (PLN — single-currency MVP).

// How many trailing days the daily series spans (today inclusive).
export const ADMIN_STATS_DAILY_DAYS = 14;

// One order-lifecycle status with its order count (for the status breakdown).
export const AdminStatsStatusCount = z.object({
  status: OrderStatusSchema,
  count: z.number().int().nonnegative(),
});
export type AdminStatsStatusCount = z.infer<typeof AdminStatsStatusCount>;

// One calendar day (UTC) of order volume + booked amount. `orders` counts orders
// CREATED that day; `amountCents` sums their totals (booked value, not just paid).
export const AdminStatsDailyPoint = z.object({
  date: z.string(), // YYYY-MM-DD (UTC)
  orders: z.number().int().nonnegative(),
  amountCents: z.number().int().nonnegative(),
});
export type AdminStatsDailyPoint = z.infer<typeof AdminStatsDailyPoint>;

// Response of `GET /admin/stats`: headline totals + a per-status breakdown + a
// trailing daily series (always ADMIN_STATS_DAILY_DAYS points, zero-filled).
export const AdminStatsResponse = z.object({
  totalOrders: z.number().int().nonnegative(),
  paidOrders: z.number().int().nonnegative(),
  paidRevenueCents: z.number().int().nonnegative(),
  currency: z.string(),
  byStatus: z.array(AdminStatsStatusCount),
  daily: z.array(AdminStatsDailyPoint),
});
export type AdminStatsResponse = z.infer<typeof AdminStatsResponse>;
